/**
 * Auth layer for the WorkOS email-code flow (SOL-123).
 *
 * Shape of the session:
 * - The **access token lives in memory only** (300s lifetime, per WorkOS).
 *   Nothing that can read storage later can read it.
 * - The **refresh token lives in `localStorage`** so a reload can resume the
 *   session. WorkOS *rotates* refresh tokens: every successful refresh
 *   returns a new one and invalidates the one just used, so the stored value
 *   must be overwritten on every refresh. Missing that logs the user out on
 *   their second refresh - roughly ten minutes in, which reads as random.
 * - The **user identity is mirrored in `localStorage`** next to it. It is not
 *   a credential (it is `{id, email}`, both already known to the user) and it
 *   has to be stored because `/api/auth/refresh` returns only tokens, and a
 *   WorkOS access token carries no email claim - so after a reload there is
 *   no other way to render "signed in as ..." without an extra round trip.
 *   It is only ever trusted for display; every authorization decision is made
 *   server-side from the verified JWT.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react'

const REFRESH_KEY = 'memoar_refresh_token'
const USER_KEY = 'memoar_user'
const API = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787'

/** Access-token lifetime as issued by WorkOS. */
const TOKEN_TTL_MS = 300_000

export interface AuthenticatedUser {
  id: string
  email: string
}

/**
 * Raised when the refresh exchange could not be *completed* - the request never
 * reached the server, or the server answered 200 with something that is not the
 * JSON we asked for (captive portal, CDN interstitial, truncated body).
 *
 * This is deliberately distinct from "the token was rejected", which resolves
 * to `null`. A user who is merely offline must not be told to sign in again:
 * their refresh token is still good and is kept in storage.
 */
export class AuthNetworkError extends Error {
  constructor(message = 'Could not reach the server. Check your connection and try again.') {
    super(message)
    this.name = 'AuthNetworkError'
  }
}

let accessToken: string | null = null
let expiresAt = 0

/**
 * Bumped by `clearSession()`. Any refresh exchange that started before the bump
 * belongs to a session the user has already dropped, so it must not write a
 * rotated refresh token back to storage, must not repopulate `accessToken`, and
 * must not hand a working bearer token back to its callers.
 */
let sessionGeneration = 0

// Declared here (rather than next to `bootstrapSession`) because
// `exchangeRefreshToken` resets it on the transport-failure path so a later
// mount can retry the bootstrap.
let bootstrapStarted = false

// ---------------------------------------------------------------------------
// Storage helpers. localStorage throws outright in some privacy modes, so
// every access is guarded - a browser that refuses storage should degrade to
// "session does not survive reload", not to a blank page.
// ---------------------------------------------------------------------------

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* storage unavailable - session simply won't survive a reload */
  }
}

function removeStorage(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    /* nothing to do */
  }
}

function readStoredUser(): AuthenticatedUser | null {
  const raw = readStorage(USER_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as unknown
    if (
      typeof parsed === 'object' && parsed !== null &&
      typeof (parsed as AuthenticatedUser).id === 'string' &&
      typeof (parsed as AuthenticatedUser).email === 'string'
    ) {
      return { id: (parsed as AuthenticatedUser).id, email: (parsed as AuthenticatedUser).email }
    }
  } catch {
    /* corrupt entry - treat as absent */
  }
  return null
}

// ---------------------------------------------------------------------------
// Tiny observable store, so `useAuth()` can be called from any component and
// they all see the same session without a provider in the tree.
// ---------------------------------------------------------------------------

export interface AuthState {
  user: AuthenticatedUser | null
  isAuthenticated: boolean
  /** True only while the initial refresh-token exchange is in flight. */
  isLoading: boolean
}

const listeners = new Set<() => void>()

function initialState(): AuthState {
  const storedUser = readStoredUser()
  const hasRefresh = readStorage(REFRESH_KEY) !== null
  if (!hasRefresh) {
    // A user record with no refresh token is a dead session; don't show it.
    if (storedUser) removeStorage(USER_KEY)
    return { user: null, isAuthenticated: false, isLoading: false }
  }
  // Resume optimistically but stay in `isLoading` until the stored refresh
  // token has actually been exchanged - otherwise a revoked session would
  // render the dashboard and then have every request 401.
  return { user: storedUser, isAuthenticated: false, isLoading: true }
}

// `useSyncExternalStore` requires a stable snapshot: this object is replaced,
// never mutated, so identity changes exactly when the session changes.
let state: AuthState = initialState()

function setState(next: AuthState): void {
  state = next
  listeners.forEach((l) => l())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): AuthState {
  return state
}

function setSession(user: AuthenticatedUser, tokens: { access_token: string; refresh_token: string }): void {
  accessToken = tokens.access_token
  expiresAt = Date.now() + TOKEN_TTL_MS
  writeStorage(REFRESH_KEY, tokens.refresh_token)
  writeStorage(USER_KEY, JSON.stringify(user))
  setState({ user, isAuthenticated: true, isLoading: false })
}

function clearSession(): void {
  accessToken = null
  expiresAt = 0
  // Invalidate any refresh exchange still in flight: without this, a refresh
  // that started before logout would resolve afterwards, re-assign
  // `accessToken` and write the rotated refresh token straight back into
  // storage - re-planting the credential the user just asked us to drop.
  sessionGeneration += 1
  refreshInFlight = null
  removeStorage(REFRESH_KEY)
  removeStorage(USER_KEY)
  setState({ user: null, isAuthenticated: false, isLoading: false })
}

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

/**
 * In-flight refresh, shared by every concurrent caller.
 *
 * This is load-bearing, not an optimisation. The dashboard fires several API
 * calls at once on mount, each of which asks for a token. Because WorkOS
 * rotates refresh tokens, two parallel refreshes would send the *same* stored
 * token twice: the first rotates it, the second gets a 401 against a token
 * that is now spent, and `clearSession()` would sign the user out mid-load.
 * Collapsing them onto one promise makes exactly one exchange happen.
 */
let refreshInFlight: Promise<string | null> | null = null

/**
 * Statuses that genuinely mean "this refresh token is dead" - and therefore
 * the only ones allowed to erase the stored credential.
 *
 * Everything else (502/503/504 from a WorkOS outage, 429 from a rate limit,
 * 500 from our own worker) says nothing about the token. Treating those as
 * rejection is how a one-minute upstream blip turns into a permanent, total
 * logout: every session refreshes within one access-token lifetime, each one
 * deletes its own refresh token, and every user has to go back through email
 * verification. The backend cooperates by answering 502 rather than 401 for
 * upstream faults (see handlers/login.ts), but this check is deliberately
 * independent of that: it is a whitelist, so any new status the backend grows
 * later fails safe.
 */
function isTokenRejected(status: number): boolean {
  return status === 401 || status === 400
}

async function exchangeRefreshToken(): Promise<string | null> {
  // Captured before the first await: everything below is checked against this
  // so a logout that lands mid-exchange wins.
  const generation = sessionGeneration

  const refresh = readStorage(REFRESH_KEY)
  if (!refresh) return null

  let body: { access_token: string; refresh_token: string }
  try {
    let sent = refresh
    let res = await fetch(`${API}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: sent }),
    })

    if (isTokenRejected(res.status)) {
      // Cross-tab rotation, not a dead session.
      //
      // Refreshes are deduped *within* a tab, but two tabs (or a duplicated
      // tab) bootstrap independently and both send the same stored token.
      // WorkOS rotates on first use, so the loser gets a 401 against a token
      // that is only stale by milliseconds - and the winner has by now written
      // the *new* token to the very storage key we are about to delete.
      // Clearing here would destroy the good credential and kill both tabs.
      //
      // So: re-read storage. If the value changed while we were in flight,
      // another tab rotated it; retry once with what it stored. Exactly once -
      // if that retry is also rejected the session really is over, and a loop
      // here would hammer the endpoint.
      const current = readStorage(REFRESH_KEY)
      if (current && current !== sent) {
        sent = current
        res = await fetch(`${API}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: sent }),
        })
      }
    }

    if (!res.ok) {
      if (isTokenRejected(res.status)) {
        // The token was actually rejected: this session is over.
        if (generation === sessionGeneration) clearSession()
        return null
      }
      // Upstream or server-side fault. Behave exactly like the transport path
      // below: keep the stored refresh token, fail this one request, and leave
      // the bootstrap re-runnable.
      bootstrapStarted = false
      throw new AuthNetworkError()
    }

    // Parsing lives inside the guard on purpose. A 200 whose body is not JSON
    // (captive portal, CDN interstitial, truncated response) is a transport
    // problem, not a rejected token, and must not escape as an unhandled
    // rejection - that used to wedge the bootstrap on "Loading..." forever.
    body = await res.json() as { access_token: string; refresh_token: string }
  } catch (err) {
    // Re-thrown from the non-OK branch above; already handled, don't re-wrap.
    if (err instanceof AuthNetworkError) throw err
    // Transport failure: keep the stored refresh token so the session survives
    // a flaky connection, let this one request fail, and leave the bootstrap
    // re-runnable so the page load is not permanently stuck signed-out.
    bootstrapStarted = false
    throw new AuthNetworkError()
  }

  // Logged out while the exchange was in flight: drop the result on the floor
  // rather than reviving a session the user ended.
  if (generation !== sessionGeneration) return null

  accessToken = body.access_token
  expiresAt = Date.now() + TOKEN_TTL_MS
  // Rotation: the token we just sent is now spent. Persist the new one.
  writeStorage(REFRESH_KEY, body.refresh_token)

  // A successful exchange proves the session is live. Restore the authenticated
  // state here as well as in `bootstrapSession()`, so a refresh that happens
  // after a failed bootstrap can recover the page without a reload.
  if (!state.isAuthenticated) {
    const user = state.user ?? readStoredUser()
    if (user) setState({ user, isAuthenticated: true, isLoading: false })
  }

  return accessToken
}

/**
 * Current access token, refreshing it when needed. Returns null when there is
 * no usable session.
 */
export async function getAccessToken(): Promise<string | null> {
  // Refresh 30s early so a request never leaves with a token that expires in
  // flight.
  if (accessToken && Date.now() < expiresAt - 30_000) return accessToken

  if (refreshInFlight) return refreshInFlight

  const pending = exchangeRefreshToken().finally(() => {
    if (refreshInFlight === pending) refreshInFlight = null
  })
  refreshInFlight = pending
  return pending
}

// ---------------------------------------------------------------------------
// Login / logout
// ---------------------------------------------------------------------------

/**
 * Ask the backend to email a six-digit code.
 *
 * Resolves for unknown addresses too - `/api/auth/start` deliberately answers
 * 200 either way so this form cannot be used to enumerate who has a paid
 * account. Callers must therefore always advance to the code step and must
 * never report an address as unknown.
 */
export async function startLogin(email: string): Promise<void> {
  const res = await fetch(`${API}/api/auth/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
  // Neutral wording on purpose. `/api/auth/start` answers 200 for unknown
  // addresses, so a non-OK response here is always our side failing (5xx, rate
  // limit, bad gateway) and never a verdict on the address. Saying "check the
  // address" would invite the user to conclude it has no account.
  if (!res.ok) throw new Error('We could not send the code right now. Please try again in a moment.')
}

/** Exchange the emailed code for a session. */
export async function verifyLogin(email: string, code: string): Promise<AuthenticatedUser> {
  const res = await fetch(`${API}/api/auth/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  })
  if (!res.ok) throw new Error('That code is invalid or has expired.')
  const body = await res.json() as {
    access_token: string
    refresh_token: string
    user: AuthenticatedUser
  }
  const user: AuthenticatedUser = { id: body.user.id, email: body.user.email }
  setSession(user, body)
  return user
}

/** Drop the session locally. */
export function logout(): void {
  clearSession()
}

// ---------------------------------------------------------------------------
// Bootstrap + hook
// ---------------------------------------------------------------------------

// Runs once per page load, from the first mounted `useAuth()` (see
// `bootstrapStarted` above). Kept out of module scope so importing this file
// (e.g. from the API client) never fires a network request on its own, and
// guarded so React StrictMode's double mount doesn't exchange the refresh
// token twice.
function bootstrapSession(): boolean {
  if (bootstrapStarted) return false
  // Already resumed, or nothing to resume. Checked against storage rather than
  // `isLoading` so that a retry after a failed bootstrap - which had to clear
  // `isLoading` to get the app off the "Loading..." screen - is still allowed
  // to run.
  if (state.isAuthenticated) return false
  if (readStorage(REFRESH_KEY) === null) return false
  bootstrapStarted = true

  void getAccessToken()
    .then((token) => {
      if (!token) {
        // exchangeRefreshToken() already cleared the session on a rejected
        // token. It may also have been superseded by a logout, in which case
        // the state is already clean.
        if (state.isLoading) setState({ user: null, isAuthenticated: false, isLoading: false })
        return
      }
      if (!state.user) {
        // A usable refresh token but no user record (storage cleared by hand,
        // or written by an older build): we can't render the session, and
        // keeping the token would repeat this exchange on every load.
        clearSession()
        return
      }
      setState({ user: state.user, isAuthenticated: true, isLoading: false })
    })
    .catch(() => {
      // Transport failure (AuthNetworkError) or anything else unexpected. The
      // refresh token is untouched and `bootstrapStarted` has been reset, so a
      // later `useAuth()` mount - or any API call that asks for a token - can
      // still recover the session. What must never happen is the rejection
      // escaping: `isLoading` would stay true and the app would render
      // "Loading..." until the user cleared storage by hand.
      if (state.isLoading) setState({ user: state.user, isAuthenticated: false, isLoading: false })
    })

  return true
}

/**
 * Re-attempt a bootstrap that failed on a transport/upstream fault.
 *
 * After such a failure the state is `{user, isAuthenticated: false,
 * isLoading: false}`: the refresh token is still in storage and
 * `bootstrapStarted` was reset, so this is just "run it again" - no reload,
 * no re-authentication. Backs the retry button App.tsx shows instead of
 * dumping a paying customer onto the signup page.
 */
export function retrySession(): void {
  // Flip to `isLoading` only if the bootstrap actually started. Setting it
  // first and having `bootstrapSession()` decline (another attempt already in
  // flight, nothing to resume) would strand the app on "Loading..." with
  // nothing left to resolve it.
  const previousIsLoading = state.isLoading
  if (!bootstrapSession()) return
  if (!previousIsLoading) setState({ ...state, isLoading: true })
}

export interface UseAuth extends AuthState {
  /** Step 1: email a login code. Always advance the UI to the code step. */
  login: (email: string) => Promise<void>
  /** Step 2: exchange the code for a session. */
  verify: (email: string, code: string) => Promise<AuthenticatedUser>
  logout: () => void
  getAccessToken: () => Promise<string | null>
  /** Re-run a bootstrap that failed on a network/upstream fault. */
  retry: () => void
}

export function useAuth(): UseAuth {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  useEffect(() => {
    bootstrapSession()
  }, [])

  const login = useCallback((email: string) => startLogin(email), [])
  const verify = useCallback((email: string, code: string) => verifyLogin(email, code), [])
  const doLogout = useCallback(() => logout(), [])
  const retry = useCallback(() => retrySession(), [])

  return {
    ...snapshot,
    login,
    verify,
    logout: doLogout,
    getAccessToken,
    retry,
  }
}
